import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faSync, faCode } from '@fortawesome/free-solid-svg-icons';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServicesSection = styled.section`
  padding: 7.2rem 5%;
  background-color: ${props => props.theme.colors.secondaryBackground};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(${props => props.theme.name === 'light'
    ? 'rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 1)'
    : 'rgba(19, 21, 26, 0.5), rgba(19, 21, 26, 1)'})
        background-size: cover;
    background-attachment: fixed;
    opacity: ${props => props.theme.name === 'light' ? '0.3' : '0.1'};
    z-index: 0;
  }
  
  @media (prefers-reduced-motion: reduce) {
    &::before {
      background-attachment: scroll; /* Meilleure performance pour ceux qui préfèrent réduire les animations */
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  text-align: center;
  margin-bottom: 0.9rem;
  position: relative;
  z-index: 1;
  color: ${props => props.theme.colors.text};
  
  &::after {
    content: '';
    display: block;
    width: 72px;
    height: 3.6px;
    background: ${props => props.theme.colors.accentGradient};
    margin: 0.9rem auto;
    border-radius: 1.8px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 630px;
  margin: 0 auto 3.6rem;
  color: ${props => props.theme.colors.secondaryText};
  position: relative;
  z-index: 1;
  line-height: 1.6;
  font-size: 0.99rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 1.8rem;
  position: relative;
  z-index: 1;
`;

const ServiceCard = styled.article`
  background: ${props => props.theme.name === 'light' ? 'white' : props.theme.colors.cardBackground};
  border-radius: 18px;
  padding: 2.25rem 1.8rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid ${props => props.theme.colors.borderColor};
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.delay || '0s'};
  will-change: transform;
  
  &:hover {
    transform: translateY(-7.2px);
    box-shadow: 0 18px 27px ${props => props.theme.name === 'light'
    ? 'rgba(0, 0, 0, 0.1)'
    : 'rgba(0, 0, 0, 0.3)'};
  }
  
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
    
    &:hover {
      transform: none;
      box-shadow: 0 4.5px 13.5px ${props => props.theme.name === 'light'
    ? 'rgba(0, 0, 0, 0.1)'
    : 'rgba(0, 0, 0, 0.3)'};
    }
  }
`;

const IconContainer = styled.div`
  width: 52.2px;
  height: 52.2px;
  background: ${props => props.theme.name === 'light' ? '#f3f0ff' : 'rgba(94, 23, 235, 0.15)'};
  border-radius: 14.4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.35rem;
  transition: all 0.3s ease;
  will-change: transform, background-color;
  
  .fa-icon {
    font-size: 25.2px;
    color: ${props => props.theme.colors.accent};
    transition: color 0s ease;
  }
  
  ${ServiceCard}:hover & {
    background: ${props => props.theme.colors.accent};
    transform: scale(1.1) rotate(-5deg);
    
    .fa-icon {
      color: white;
    }
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    
    ${ServiceCard}:hover & {
      transform: none;
    }
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.35rem;
  margin-bottom: 0.9rem;
  color: ${props => props.theme.colors.text};
`;

const ServiceDescription = styled.p`
  color: ${props => props.theme.colors.secondaryText};
  line-height: 1.6;
`;

// Composant pour cacher visuellement du texte pour les lecteurs d'écran
const VisuallyHidden = styled.span`
  position: absolute !important;
  height: 0.9px;
  width: 0.9px;
  overflow: hidden;
  clip: rect(0.9px 0.9px 0.9px 0.9px); /* IE6, IE7 */
  clip: rect(0.9px, 0.9px, 0.9px, 0.9px);
  white-space: nowrap;
`;

const ServicesCallToAction = styled.div`
  margin-top: 3.6rem;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 0.9rem 2.25rem;
  background: ${props => props.theme.colors.accentGradient};
  color: white;
  border-radius: 45px;
  font-weight: 600;
  font-size: 0.99rem;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover, &:focus {
    transform: translateY(-2.7px);
    box-shadow: 0 9px 18px rgba(94, 23, 235, 0.3);
  }
  
  &:focus {
    outline: 2.7px solid ${props => props.theme.colors.accent};
    outline-offset: 2.7px;
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    
    &:hover, &:focus {
      transform: none;
    }
  }
`;

const Services = () => {
  const services = [
    {
      id: "site-vitrine",
      title: "Création de site web",
      description: "Sites vitrines professionnels et percutants qui reflètent parfaitement l'identité de votre entreprise et convertissent les visiteurs en clients.",
      icon: faGlobe,
      delay: "0s",
      moreInfo: "Design responsive, optimisé pour le SEO et intégrant les meilleures pratiques UX/UI."
    },
    {
      id: "refonte-site",
      title: "Refonte de site existant",
      description: "Modernisation de votre présence en ligne avec un nouveau design, une expérience utilisateur améliorée et des performances optimisées.",
      icon: faSync,
      delay: "0.2s",
      moreInfo: "Analyse de l'existant, refonte graphique, optimisation technique et migration sans perte de référencement."
    },
    {
      id: "developpement-mesure",
      title: "Développement sur mesure",
      description: "Solutions web adaptées à vos besoins spécifiques avec des fonctionnalités personnalisées et une architecture optimisée.",
      icon: faCode,
      delay: "0.4s",
      moreInfo: "Développement de fonctionnalités spécifiques, intégration de systèmes tiers et solutions personnalisées."
    }
  ];

  return (
    <ServicesSection id="services" aria-labelledby="services-title">
      <SectionTitle id="services-title">Nos Services</SectionTitle>
      <SectionSubtitle>
        Nous créons des sites web professionnels qui vous distinguent de la concurrence et aident votre entreprise à atteindre ses objectifs commerciaux grâce à une présence en ligne efficace.
      </SectionSubtitle>

      <ServicesGrid>
        {services.map((service) => (
          <ServiceCard
            key={service.id}
            delay={service.delay}
          >
            <IconContainer aria-hidden="true">
              <FontAwesomeIcon icon={service.icon} className="fa-icon" />
            </IconContainer>
            <ServiceTitle id={`service-title-${service.id}`}>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
          </ServiceCard>
        ))}
      </ServicesGrid>

      <ServicesCallToAction>
        <CTAButton
          href="#contact"
          aria-label="Discuter de votre projet de site web avec notre équipe"
        >
          Discuter de votre projet
        </CTAButton>
      </ServicesCallToAction>
    </ServicesSection>
  );
};

export default Services;